import Button from '../Button';
import React, { useCallback, useContext } from 'react';
import useTranslations from '../../hooks/useTranslations';
import _SlimLineCard from '../containers/cards/SlimLineCard';
import { getButtonStyle } from '../engine/CustomButtonWizardEngine';
import {
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import styled from 'styled-components/macro';
import Link from '../Link';
import { AutomationsContex } from '../engine/FormBuilderAutomationsEngine';
import generateId from '../../utils/generateId';
import ActionsTooltip from '../ActionsTooltip';
import {
  BUTTON_CATEGORIES,
  CustomButtonsContext,
  SYSTEM_BUTTONS_DEFAULT_SCHEMA
} from '../engine/FormBuilderCustomButtonsEngine';
import Deleteable from '../Deleteable';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import OfflinePinIcon from '@material-ui/icons/OfflinePin';
import { Draggable } from 'react-beautiful-dnd';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import Disabler from '../Disabler';

const SlimLineCard = styled(_SlimLineCard)`
  margin-bottom: 1rem;
`;

const ElementsContainerCSS = `
transition: 0.5s;
display: flex;
align-items: center;
& > span {
  width: 20%;
  // text-align: center;
}
`;

const Span = styled.span`
  ${({ $width }) => $width && `width: ${$width} !important;`}
  ${({ $customCSS }) => $customCSS}
`;

const FormBuilderCustomButtonCardView = props => {
  const t = useTranslations();
  const [statusesText, statusesTooltipText] = props.getStatusesColumnData();
  const {
    onWizardOpned,
    btn2Delete,
    initButtonDeleteProccess,
    onButtonDelete,
    updateSingleButton
  } = useContext(CustomButtonsContext);

  return (
    <Draggable draggableId={props.id} index={props.position}>
      {provided => (
        <Deleteable
          appear
          onDelete={() => onButtonDelete(props.id)}
          deleteTrigger={btn2Delete === props.id}
        >
          <ActionsTooltip
            leaveDelay={0}
            buttons={
              <>
                <Tooltip title={t('customButtons.list.tooltip.edit')}>
                  <IconButton
                    size="small"
                    onClick={() => onWizardOpned(props.buttonData)}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                {props.disabled ? (
                  <Tooltip title={t('customButtons.list.tooltip.enable')}>
                    <IconButton
                      size="small"
                      onClick={() =>
                        updateSingleButton({
                          ...props.buttonData,
                          statuses: ['all']
                        })
                      }
                    >
                      <OfflinePinIcon />
                    </IconButton>
                  </Tooltip>
                ) : props.noDisable ? (
                  ''
                ) : (
                  <Tooltip title={t('customButtons.list.tooltip.disable')}>
                    <IconButton
                      size="small"
                      onClick={() =>
                        updateSingleButton({
                          ...props.buttonData,
                          statuses: []
                        })
                      }
                    >
                      <RemoveCircleIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {props.buttonCategory !== BUTTON_CATEGORIES.CUSTOM ? null : (
                  <Tooltip title={t('customButtons.list.tooltip.delete')}>
                    <IconButton
                      size="small"
                      onClick={() => initButtonDeleteProccess(props.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            }
          >
            <SlimLineCard
              $rounded
              // $disabled={props.disabled}
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
            >
              <Disabler
                disabled={props.disabled}
                containerCSS={ElementsContainerCSS}
              >
                <Span
                  $width="5%"
                  $customCSS="display: flex; align-items: center; opacity: 0.5;"
                >
                  <DragIndicatorIcon />
                </Span>
                <Span $width="30%">
                  <Button
                    variant={props.variant}
                    customCSS={getButtonStyle(props.variant, props.color)}
                  >
                    {t(props.label)}
                  </Button>
                </Span>
                <Span>{t(`systemBtn.type.${props.type}`)}</Span>
                <Span>
                  <Tooltip title={statusesTooltipText}>
                    <span>{statusesText}</span>
                  </Tooltip>
                </Span>
                <Span>
                  <WhenClicked
                    btnId={props.id}
                    automationsUsingBtn={props.automationsUsingBtn}
                  />
                </Span>
                <Span $width="15%">
                  <Chip
                    label={t(
                      `customButtons.list.card.chip.${props.buttonCategory}`
                    )}
                    color="primary"
                    variant={
                      props.buttonCategory === BUTTON_CATEGORIES.SYSTEM
                        ? 'outlined'
                        : 'default'
                    }
                  />
                </Span>
              </Disabler>
            </SlimLineCard>
          </ActionsTooltip>
        </Deleteable>
      )}
    </Draggable>
  );
};

const TooltipLink = styled(Link)`
  && {
    // color: #fff;
    text-align: left;
  }
`;

const WhenClicked = ({ automationsUsingBtn, btnId }) => {
  const t = useTranslations();
  const { openWizard } = useContext(AutomationsContex);

  const automationsIdTranslationsMap = {
    // match automations trigger names to system buttons ids
    [SYSTEM_BUTTONS_DEFAULT_SCHEMA.SUBMIT.id]: 'submitted',
    [SYSTEM_BUTTONS_DEFAULT_SCHEMA.SIGN_AND_SUBMIT.id]: 'submitted',
    [SYSTEM_BUTTONS_DEFAULT_SCHEMA.SAVE_CHANGES.id]: 'admin_save_changes',
    [SYSTEM_BUTTONS_DEFAULT_SCHEMA.REQUEST_MORE_INFO.id]: 'request_more_info'
  };

  const getWhen = useCallback(btnId => {
    const backEndId = automationsIdTranslationsMap[btnId];
    if (backEndId) {
      return { eventType: backEndId };
    }

    return { eventType: 'cb', eventInfo: btnId };
  }, []);

  const basicStructure = {
    label: '',
    rule: {
      when: getWhen(btnId),
      actionsList: []
    },
    position: -1,
    internalId: generateId()
  };

  return !automationsUsingBtn.length ? (
    <Link
      onClick={() => {
        openWizard(basicStructure);
      }}
    >
      {t('customButtons.list.addAutomation')}
    </Link>
  ) : (
    <ActionsTooltip
      leaveDelay={0}
      placement="bottom"
      variant="white"
      buttons={
        <List dense>
          {automationsUsingBtn.map(a => (
            <ListItem key={a.internalId}>
              <ListItemText
                primary={
                  <TooltipLink
                    onClick={() => {
                      openWizard(a);
                    }}
                  >
                    {t(a.label)}
                  </TooltipLink>
                }
              />
            </ListItem>
          ))}
        </List>
      }
    >
      <span>View automation </span>
    </ActionsTooltip>
  );
};

export default FormBuilderCustomButtonCardView;
